// Janela "Nova cidade" (DA-878). A cidade se ESCOLHE da lista oficial do IBGE (UF + municipio) —
// nao se digita: o nome e o codigo vem juntos, e o servidor confere pelo codigo. Abre da tela de
// Cidades e do "+" do campo Cidade no cadastro de cliente (por isso o overlay e' z-[1100]: abre
// de dentro de outro modal).
import { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { Plus } from 'lucide-react';
import { api, ApiError } from '../../lib/api';
import { ComboBox } from '../ComboBox';

export interface Cidade {
  id: number;
  codigo_ibge: string;
  nome: string;
  uf: string;
  clientes?: number;
}

const UFS = [
  'AC', 'AL', 'AM', 'AP', 'BA', 'CE', 'DF', 'ES', 'GO', 'MA', 'MG', 'MS', 'MT', 'PA',
  'PB', 'PE', 'PI', 'PR', 'RJ', 'RN', 'RO', 'RR', 'RS', 'SC', 'SE', 'SP', 'TO',
];

/** Cadastra (ou devolve a ja cadastrada) pelo codigo IBGE. Usado tambem pelo CEP e pela SEFAZ. */
export async function garantirCidade(codigoIbge: string): Promise<Cidade> {
  return api.post<Cidade>('/api/cidades', { codigo_ibge: codigoIbge });
}

interface Props {
  onSalvo: (cidade: Cidade) => void;
  onFechar: () => void;
  /** Cidades ja cadastradas: aparecem na lista em cinza ("ja cadastrada"), nao somem. */
  jaCadastradas?: string[];
}

export function ModalNovaCidade({ onSalvo, onFechar, jaCadastradas = [] }: Props) {
  const [uf, setUf] = useState('GO');
  const [municipios, setMunicipios] = useState<{ codigo_ibge: string; nome: string }[] | null>(null);
  const [erroLista, setErroLista] = useState('');
  const [codigo, setCodigo] = useState('');
  const [salvando, setSalvando] = useState(false);
  const [erro, setErro] = useState('');

  useEffect(() => {
    let vivo = true;
    setMunicipios(null);
    setErroLista('');
    setCodigo('');
    api.get<{ codigo_ibge: string; nome: string }[]>(`/api/cidades/ibge/${uf}`)
      .then((l) => { if (vivo) setMunicipios(l); })
      .catch((e) => { if (vivo) setErroLista(e instanceof ApiError ? e.message : 'Nao consegui carregar a lista do IBGE.'); });
    return () => { vivo = false; };
  }, [uf]);

  useEffect(() => {
    const esc = (e: KeyboardEvent) => { if (e.key === 'Escape') onFechar(); };
    window.addEventListener('keydown', esc);
    return () => window.removeEventListener('keydown', esc);
  }, [onFechar]);

  async function salvar() {
    if (!codigo) return;
    setErro('');
    setSalvando(true);
    try {
      onSalvo(await garantirCidade(codigo));
    } catch (e) {
      setErro(e instanceof ApiError ? e.message : 'Erro ao cadastrar a cidade.');
    } finally {
      setSalvando(false);
    }
  }

  const ja = new Set(jaCadastradas);

  return createPortal(
    <div className="fixed inset-0 flex items-center justify-center z-[1100] p-4" style={{ backgroundColor: 'rgba(0,0,0,0.4)' }}>
      <div className="rounded-xl shadow-xl w-full" style={{ backgroundColor: 'var(--panel-bg)', maxWidth: 460 }}>
        <div className="flex items-center justify-between px-5 py-4 border-b border-gray-200">
          <h3 className="font-semibold text-gray-800 flex items-center gap-2">
            <Plus size={16} /> Nova cidade
          </h3>
          <button type="button" className="btn-fechar-modal" onClick={onFechar} aria-label="Fechar">×</button>
        </div>

        <div className="p-5" style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
          <label style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
            UF
            <select value={uf} onChange={(e) => setUf(e.target.value)} style={{ maxWidth: 120 }}>
              {UFS.map((u) => <option key={u} value={u}>{u}</option>)}
            </select>
          </label>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
            <span>Cidade (lista oficial do IBGE)</span>
            {erroLista ? (
              <span style={{ fontSize: 13, color: '#b91c1c', fontWeight: 600 }}>{erroLista}</span>
            ) : (
              <ComboBox
                value={codigo || null}
                onChange={(v) => setCodigo(v === '' || v == null ? '' : String(v))}
                opcoes={(municipios ?? []).map((m) => ({
                  value: m.codigo_ibge,
                  label: m.nome,
                  desabilitada: ja.has(m.codigo_ibge),
                  nota: ja.has(m.codigo_ibge) ? 'ja cadastrada' : undefined,
                }))}
                placeholder={municipios ? 'Digite pra procurar a cidade...' : 'Carregando a lista do IBGE...'}
                disabled={!municipios}
                autoFocus
              />
            )}
            {codigo && <span style={{ fontSize: 12, color: 'var(--text-muted)' }}>Codigo IBGE {codigo}</span>}
          </div>

          {erro && <div style={{ fontSize: 13, color: '#b91c1c', fontWeight: 600 }}>{erro}</div>}

          <div className="flex justify-end gap-2" style={{ paddingTop: 4 }}>
            <button type="button" className="btn-secundario" onClick={onFechar}>Cancelar</button>
            <button type="button" className="btn-primario" onClick={salvar} disabled={!codigo || salvando}>
              {salvando ? 'Salvando...' : 'Salvar'}
            </button>
          </div>
          {!codigo && municipios && (
            <span style={{ fontSize: 12, color: 'var(--text-muted)', textAlign: 'right' }}>Escolha a cidade pra salvar</span>
          )}
        </div>
      </div>
    </div>,
    document.body,
  );
}
